// Sales Documents Types and Interfaces

// Status Enums
export enum QuotationStatus {
  DRAFT = "Draft",
  SENT = "Sent",
  ACCEPTED = "Accepted",
  REJECTED = "Rejected",
  EXPIRED = "Expired",
  CONVERTED = "Converted",
}

export enum SalesOrderStatus {
  DRAFT = "Draft",
  CONFIRMED = "Confirmed",
  PROCESSING = "Processing",
  PARTIALLY_DELIVERED = "Partially Delivered",
  DELIVERED = "Delivered",
  PARTIALLY_FULFILLED = "Partially Fulfilled",
  FULFILLED = "Fulfilled",
  CANCELLED = "Cancelled",
}

export enum DeliveryOrderStatus {
  DRAFT = "Draft",
  PENDING = "Pending",
  IN_TRANSIT = "In Transit",
  DELIVERED = "Delivered",
  INVOICE_PENDING = "Invoice Pending",
  ISSUED_INVOICE = "Issued Invoice",
  CANCELLED = "Cancelled",
}

// Line Item Interface (shared across all documents)
export interface SalesDocumentLineItem {
  id?: string;
  itemId?: string;
  itemCode: string;
  classification: string;
  description: string;
  quantity: number;
  unitPrice: number;
  measurement: string | null;
  discountRate: number | null;
  discountAmount: number | null;
  feeChargeRate?: number | null;
  feeChargeAmount?: number | null;
  taxType: "01" | "02" | "03" | "04" | "05" | "06" | "E";
  taxRate: number;
  taxAmount: number;
  subtotal: number;
  totalExcludingTax: number;
  total: number;
  detailsOfTaxExemption?: string | null;
  amountExemptedFromTax?: number | null;
  productTariffCode?: string | null;
  countryOfOrigin?: string | null;
  _delete?: boolean;
}

// Shipping Information Interface
export interface ShippingInformation {
  id?: string | null;
  recipientName?: string | null;
  recipientAddressLine0?: string | null;
  recipientAddressLine1?: string | null;
  recipientAddressLine2?: string | null;
  recipientPostcode?: string | null;
  recipientCity?: string | null;
  recipientCountry?: string | null;
  recipientState?: string | null;
  recipientRegistrationType?: string | null;
  recipientRegistrationNumber?: string | null;
  recipientTin?: string | null;
}

// Quotation Interfaces
export interface Quotation {
  id: string;
  codeNumber: string;
  quotationDate: Date;
  quotationTime: Date;
  validUntil: Date;
  currencyCode: string;
  currencyExchangeRate: number | null;
  status: QuotationStatus;
  jobCode?: string | null;
  docDate?: Date | null;

  // Buyer Information
  buyerCode: string | null;
  buyerRegistrationType: string;
  buyerRegistrationNumber: string;
  buyerTin: string;
  buyerName: string;
  buyerSstRegistrationNumber: string | null;
  buyerEmail: string | null;
  buyerContact: string;
  buyerAddressLine0: string;
  buyerAddressLine1: string | null;
  buyerAddressLine2: string | null;
  buyerCity: string;
  buyerPostcode: string | null;
  buyerState: string;
  buyerCountry: string;

  // Line Items
  lineItems: SalesDocumentLineItem[];

  // Totals
  totalExcludingTax: number;
  totalTaxAmount: number;
  totalIncludingTax: number;
  totalPayableAmount: number;
  totalNetAmount: number | null;
  totalDiscountValue: number | null;
  totalFeeChargeAmount: number | null;
  roundingAmount: number | null;

  // Additional Fields
  additionalDiscountAmount: number | null;
  additionalFeeAmount: number | null;
  termsAndConditions: string | null;
  remarks: string | null;

  // Shipping Information
  shipping?: ShippingInformation | null;

  // Email-generated metadata
  isEmailGenerated?: boolean;
  sourceEmailId?: string | null;

  // Metadata
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  convertedToSalesOrderId?: string | null;
}

export interface QuotationListItem {
  id: string;
  codeNumber: string;
  quotationDate: string;
  quotationTime: string;
  validUntil: string;
  buyerCode: string;
  buyerName: string | null;
  totalPayableAmount: number;
  status: QuotationStatus;
  isConverted: boolean;
  salesOrderId: string | null;
  emailStatus: string | null;
}

// Sales Order Interfaces
export interface SalesOrder {
  id: string;
  codeNumber: string;
  orderDate: Date;
  orderTime: Date;
  expectedDeliveryDate: Date | null;
  customerPONumber: string | null;
  currencyCode: string;
  currencyExchangeRate: number | null;
  status: SalesOrderStatus;
  jobCode?: string | null;
  docDate?: Date | null;

  // Reference to Quotation (if created from quotation)
  quotationId: string | null;
  quotationNumber: string | null;

  // Buyer Information
  buyerCode: string | null;
  buyerRegistrationType: string;
  buyerRegistrationNumber: string;
  buyerTin: string;
  buyerName: string;
  buyerSstRegistrationNumber: string | null;
  buyerEmail: string | null;
  buyerContact: string;
  buyerAddressLine0: string;
  buyerAddressLine1: string | null;
  buyerAddressLine2: string | null;
  buyerCity: string;
  buyerPostcode: string | null;
  buyerState: string;
  buyerCountry: string;

  // Line Items
  lineItems: SalesOrderLineItem[];

  // Totals
  totalExcludingTax: number;
  totalTaxAmount: number;
  totalIncludingTax: number;
  totalPayableAmount: number;
  totalNetAmount: number | null;
  totalDiscountValue: number | null;
  totalFeeChargeAmount: number | null;
  roundingAmount: number | null;

  // Additional Fields
  additionalDiscountAmount: number | null;
  additionalFeeAmount: number | null;
  termsAndConditions: string | null;
  remarks: string | null;

  // Shipping Information
  shipping?: ShippingInformation | null;

  // Metadata
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
}

export interface SalesOrderLineItem extends SalesDocumentLineItem {
  deliveredQuantity: number;
  remainingQuantity: number;
}

export interface SalesOrderListItem {
  id: string;
  codeNumber: string;
  orderDate: string;
  orderTime: string;
  expectedDeliveryDate: string | null;
  customerPONumber: string | null;
  buyerCode: string;
  buyerName: string | null;
  totalPayableAmount: number;
  status: SalesOrderStatus;
  quotationNumber: string | null;
  hasDeliveries: boolean;
  deliveryStatus: "Not Started" | "Partial" | "Complete";
  emailStatus: string | null;
}

// Delivery Order Interfaces
export interface DeliveryOrder {
  id: string;
  codeNumber: string;
  deliveryDate: Date;
  deliveryTime: Date;
  currencyCode: string;
  currencyExchangeRate: number | null;
  status: DeliveryOrderStatus;
  jobCode?: string | null;
  docDate?: Date | null;

  // Reference to Sales Order
  salesOrderId: string;
  salesOrderNumber: string;

  // Buyer Information
  buyerCode: string | null;
  buyerRegistrationType: string;
  buyerRegistrationNumber: string;
  buyerTin: string;
  buyerName: string;
  buyerSstRegistrationNumber: string | null;
  buyerEmail: string | null;
  buyerContact: string;
  buyerAddressLine0: string;
  buyerAddressLine1: string | null;
  buyerAddressLine2: string | null;
  buyerCity: string;
  buyerPostcode: string | null;
  buyerState: string;
  buyerCountry: string;

  // Delivery Information
  carrierName: string | null;
  trackingNumber: string | null;
  vehicleNumber: string | null;
  driverName: string | null;
  driverContact: string | null;
  deliveryInstructions: string | null;

  // Line Items
  lineItems: DeliveryOrderLineItem[];

  // Totals
  totalExcludingTax: number;
  totalTaxAmount: number;
  totalIncludingTax: number;
  totalPayableAmount: number;
  totalNetAmount: number | null;
  totalDiscountValue: number | null;
  totalFeeChargeAmount: number | null;
  roundingAmount: number | null;

  // Additional Fields
  additionalDiscountAmount: number | null;
  additionalFeeAmount: number | null;
  remarks: string | null;

  // Shipping Information
  shipping?: ShippingInformation | null;

  // Metadata
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
}

export interface DeliveryOrderLineItem extends SalesDocumentLineItem {
  salesOrderLineItemId: string;
  orderedQuantity: number; // From sales order
  availableQuantity: number; // Remaining to deliver
}

export interface DeliveryOrderListItem {
  id: string;
  codeNumber: string;
  deliveryDate: string;
  deliveryTime: string;
  salesOrderNumber: string;
  sourceSalesOrderId: string | null;
  buyerCode: string;
  buyerName: string | null;
  totalPayableAmount: number;
  status: DeliveryOrderStatus;
  trackingNumber: string | null;
  carrierName: string | null;
  emailStatus: string | null;
}

// API Request/Response Types
export interface CreateQuotationRequest {
  codeNumber: string;
  quotationDate: string;
  quotationTime: string;
  validUntilDate?: string | null;
  validUntil?: string;
  currencyCode: string;
  currencyExchangeRate?: number | null;
  prefixId?: string;
  docDate?: Date | null;
  jobCode?: string | null;

  // Buyer Info
  buyerCode?: string | null;
  buyerRegistrationType: string;
  buyerRegistrationNumber: string;
  buyerTin: string;
  buyerName: string;
  buyerSstRegistrationNumber?: string | null;
  buyerEmail?: string | null;
  buyerContact: string;
  buyerAddressLine0: string;
  buyerAddressLine1?: string | null;
  buyerAddressLine2?: string | null;
  buyerCity: string;
  buyerPostcode?: string | null;
  buyerState: string;
  buyerCountry: string;

  lineItems: Omit<SalesDocumentLineItem, "id">[];

  totalExcludingTax?: number;
  totalIncludingTax?: number;
  totalPayableAmount?: number;
  totalNetAmount?: number | null;
  totalDiscountValue?: number | null;
  totalFeeChargeAmount?: number | null;
  totalTaxAmount?: number;
  roundingAmount?: number | null;
  totalTaxableAmountPerTaxType?: any;
  totalTaxAmountPerTaxType?: any;
  taxType?: string;

  quotationAdditionalDiscountAmount?: number | null;
  quotationAdditionalFeeAmount?: number | null;
  additionalDiscountAmount?: number | null;
  additionalFeeAmount?: number | null;
  termsAndConditions?: string | null;
  remarks?: string | null;
  shipping?: ShippingInformation | null;

  // Email-generated metadata
  isEmailGenerated?: boolean;
  sourceEmailId?: string | null;
}

export interface CreateSalesOrderRequest {
  codeNumber: string;
  orderDate: string;
  orderTime: string;
  expectedDeliveryDate?: string | null;
  customerPONumber?: string | null;
  currencyCode: string;
  currencyExchangeRate?: number | null;
  quotationId?: string | null;
  prefixId?: string;

  // Buyer Info
  buyerCode?: string | null;
  buyerRegistrationType: string;
  buyerRegistrationNumber: string;
  buyerTin: string;
  buyerName: string;
  buyerSstRegistrationNumber?: string | null;
  buyerEmail?: string | null;
  buyerContact: string;
  buyerAddressLine0: string;
  buyerAddressLine1?: string | null;
  buyerAddressLine2?: string | null;
  buyerCity: string;
  buyerPostcode?: string | null;
  buyerState: string;
  buyerCountry: string;

  lineItems: Omit<SalesDocumentLineItem, "id">[];

  additionalDiscountAmount?: number | null;
  additionalFeeAmount?: number | null;
  termsAndConditions?: string | null;
  remarks?: string | null;
}

export interface CreateDeliveryOrderRequest {
  codeNumber: string;
  deliveryDate: string;
  deliveryTime: string;
  sourceSalesOrderId?: string;
  currencyCode: string;
  currencyExchangeRate?: number | null;
  prefixId?: string;
  docDate?: Date | null;
  jobCode?: string | null;

  // Buyer Info
  buyerCode?: string | null;
  buyerRegistrationType: string;
  buyerRegistrationNumber: string;
  buyerTin: string;
  buyerName: string;
  buyerSstRegistrationNumber?: string | null;
  buyerEmail?: string | null;
  buyerContact: string;
  buyerAddressLine0: string;
  buyerAddressLine1?: string | null;
  buyerAddressLine2?: string | null;
  buyerCity: string;
  buyerPostcode?: string | null;
  buyerState: string;
  buyerCountry: string;

  carrierName?: string | null;
  trackingNumber?: string | null;
  vehicleNumber?: string | null;
  driverName?: string | null;
  driverContact?: string | null;
  deliveryInstructions?: string | null;

  lineItems: Omit<SalesDocumentLineItem, "id">[];

  totalExcludingTax?: number;
  totalIncludingTax?: number;
  totalPayableAmount?: number;
  totalNetAmount?: number | null;
  totalDiscountValue?: number | null;
  totalFeeChargeAmount?: number | null;
  totalTaxAmount?: number;
  roundingAmount?: number | null;
  totalTaxableAmountPerTaxType?: any;
  totalTaxAmountPerTaxType?: any;
  taxType?: string;

  deliveryOrderAdditionalDiscountAmount?: number | null;
  deliveryOrderAdditionalFeeAmount?: number | null;
  additionalDiscountAmount?: number | null;
  additionalFeeAmount?: number | null;
  remarks?: string | null;
  shipping?: ShippingInformation | null;
}

export interface ConvertQuotationToSalesOrderRequest {
  quotationId: string;
  orderDate?: string;
  expectedDeliveryDate?: string | null;
  customerPONumber?: string | null;
  prefixId?: string;
}

export interface CreateDeliveryOrderFromSalesOrderRequest {
  salesOrderId: string;
  codeNumber?: string;
  deliveryDate?: string;
  deliveryTime?: string;
  docDate?: string;
  carrierName?: string | null;
  trackingNumber?: string | null;
  vehicleNumber?: string | null;
  driverName?: string | null;
  driverContact?: string | null;
  remarks?: string | null;
  deliveryInstructions?: string | null;
  lineItems?: {
    salesOrderLineItemId: string;
    quantity: number;
  }[];
}

// API Response Types
export interface SalesDocumentApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface SalesDocumentListResponse<T> {
  data: T[];
  total: number;
  page?: number;
  limit?: number;
}
